import type { GetStaticPaths, GetStaticProps, NextPage } from "next";
import Link from "next/link";
import { useTranslation } from "next-i18next/pages";
import SubpageShell from "components/common/SubpageShell";
import CaseGallery from "components/case-studies/CaseGallery";
import Testimonial from "components/home/Testimonial";
import {
  getCaseStudy,
  getCaseStudySlugs,
  type CaseFact,
  type CaseSection,
  type CaseStudy
} from "lib/cms/caseStudies";
import {
  CMS_REVALIDATE_SECONDS,
  serverSideCmsTranslations
} from "lib/cms/translations";
import styles from "../../styles/case-studies/CaseStudies.module.css";

interface CaseStudyDetailProps {
  study: CaseStudy;
}

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const slugs = await getCaseStudySlugs();
  const paths = slugs.flatMap((slug) =>
    (locales ?? ["de"]).map((locale) => ({ params: { slug }, locale }))
  );

  return { paths, fallback: "blocking" };
};

export const getStaticProps: GetStaticProps<CaseStudyDetailProps> = async ({
  locale,
  params
}) => {
  const slug = params?.slug as string;
  const pageLocale = locale ?? "de";
  const study = await getCaseStudy(slug, pageLocale);

  if (!study) {
    // Without revalidate, a 404 rendered while the study is unpublished
    // would be cached until the next deploy.
    return {
      notFound: true,
      revalidate: CMS_REVALIDATE_SECONDS
    };
  }

  return {
    props: {
      study,
      ...(await serverSideCmsTranslations(pageLocale, [
        "common",
        "case-studies"
      ]))
    },
    revalidate: CMS_REVALIDATE_SECONDS
  };
};

const CaseStudyDetailPage: NextPage<CaseStudyDetailProps> = ({ study }) => {
  const { t } = useTranslation("case-studies");

  const renderSection = (section: CaseSection) => (
    <div className={styles.block}>
      <h3 className={styles.blockTitle}>{section.title}</h3>
      {section.paragraphs.map((paragraph: string) => (
        <p key={paragraph} className={styles.paragraph}>
          {paragraph}
        </p>
      ))}
      {
        section.bullets &&
        <ul className={styles.bulletList}>
          {section.bullets.map((bullet: string) => (
            <li key={bullet} className={styles.bullet}>
              {bullet}
            </li>
          ))}
        </ul>
      }
    </div>
  );

  return (
    <SubpageShell
      title={`${study.title} — Browserbite`}
      description={study.summary}
      path={`/case-studies/${study.id}`}
      eyebrow={study.industry}
      heading={study.title}
      subtitle={study.summary}
      doAnimation
    >
      <div className={styles.container}>
        <Link href="/case-studies" className={styles.backLink}>
          ← {t("backToOverview")}
        </Link>

        <article className={styles.study}>
          {
            study.disclaimer &&
            <p className={styles.disclaimer}>{study.disclaimer}</p>
          }

          <div className={styles.factGrid}>
            {study.facts.map((fact: CaseFact) => (
              <div key={fact.label} className={styles.factCard}>
                <div className={styles.factValue}>{fact.value}</div>
                <div className={styles.factLabel}>{fact.label}</div>
              </div>
            ))}
          </div>

          {renderSection(study.challenge)}
          {renderSection(study.solution)}

          {
            study.gallery &&
            <CaseGallery
              title={t("galleryTitle")}
              images={study.gallery}
              note={study.galleryNote}
              enlargeLabel={t("gallery.enlarge")}
              closeLabel={t("gallery.close")}
              prevLabel={t("gallery.prev")}
              nextLabel={t("gallery.next")}
            />
          }

          {
            study.mockup &&
            <CaseGallery
              title={t("mockupTitle")}
              images={study.mockup}
              note={study.mockupNote}
              enlargeLabel={t("gallery.enlarge")}
              closeLabel={t("gallery.close")}
              prevLabel={t("gallery.prev")}
              nextLabel={t("gallery.next")}
            />
          }

          {renderSection(study.results)}
        </article>

        <div className={styles.ctaPanel}>
          <h2 className={styles.ctaTitle}>{t("cta.title")}</h2>
          <p className={styles.ctaText}>{t("cta.text")}</p>
          <Link href="/contact" className={styles.ctaButton}>
            {t("cta.button")}
          </Link>
        </div>
      </div>

      <Testimonial doAnimation />
    </SubpageShell>
  );
};

export default CaseStudyDetailPage;
